/**
 * User Table Component
 *
 * Displays organization users in a responsive table format with role badges
 * and password reset actions.
 */

import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { OrganizationUser } from '@/types/user-management.types';
import { UserRole } from '@/types/enums/UserRole';
import { Avatar } from '@/components/ui/avatar';
import { Icon, IconName } from '@/icons';
import { PasswordResetPopup } from './PasswordResetPopup';
import { generateDeepLink, DeepLinkEntity } from '@/utils/deep-linking';
import { logger } from '@/utils/logger';

export interface UserTableProps {
  /** Users to display in the table */
  users: OrganizationUser[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Callback when password reset is requested */
  onPasswordReset: (
    userId: string,
    userEmail: string,
    newPassword: string
  ) => Promise<void>;
  /** Callback when a row is clicked */
  onRowClick?: (user: OrganizationUser) => void;
  /** Callback when delete is clicked */
  onDelete?: (user: OrganizationUser) => void;
}

type SortKey = 'username' | 'display_name' | 'email' | 'role';
type SortDirection = 'asc' | 'desc';

/**
 * Get badge variant for user role
 */
function getRoleBadgeVariant(role: UserRole): 'purple' | 'blue' | 'green' {
  switch (role) {
    case UserRole.ADMIN:
      return 'purple';
    case UserRole.MENTOR:
      return 'blue';
    case UserRole.STUDENT:
      return 'green';
  }
}

/**
 * Loading skeleton for table rows
 */
function TableSkeleton() {
  return (
    <>
      {[1, 2, 3].map(i => (
        <TableRow key={i}>
          <TableCell>
            <div className="h-8 w-8 bg-muted animate-pulse rounded-full" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-24" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-32" />
          </TableCell>
          <TableCell>
            <div className="h-4 bg-muted animate-pulse rounded w-40" />
          </TableCell>
          <TableCell>
            <div className="h-6 bg-muted animate-pulse rounded-full w-16" />
          </TableCell>
          <TableCell>
            <div className="h-9 bg-muted animate-pulse rounded w-28" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

/**
 * Empty state when no users match filters
 */
function EmptyState() {
  return (
    <TableRow>
      <TableCell colSpan={6} className="h-24 text-center">
        <div className="flex flex-col items-center justify-center text-muted-foreground">
          <p className="text-sm">No users found</p>
          <p className="text-xs mt-1">Try adjusting your filters</p>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * User Table Component
 *
 * Displays users in a table with role badges, sorting, and password reset functionality.
 */
export function UserTable({
  users,
  isLoading = false,
  onPasswordReset,
  onRowClick,
  onDelete,
}: UserTableProps) {
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    email: string | null;
  } | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: SortDirection;
  } | null>(null);

  // Handle sorting
  const handleSort = (key: SortKey) => {
    let direction: SortDirection = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort users
  const sortedUsers = [...users].sort((a, b) => {
    if (!sortConfig) return 0;

    const { key, direction } = sortConfig;
    const aValue = a[key] ?? '';
    const bValue = b[key] ?? '';

    if (aValue < bValue) {
      return direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Get sort indicator
  const getSortIndicator = (key: SortKey) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const handleResetClick = (
    e: React.MouseEvent,
    userId: string,
    userEmail: string | null
  ) => {
    e.stopPropagation(); // Prevent row click
    setSelectedUser({ id: userId, email: userEmail });
  };

  const handleResetConfirm = async (email: string, newPassword: string) => {
    if (!selectedUser) return;

    setIsResetting(true);
    try {
      await onPasswordReset(selectedUser.id, email, newPassword);
      setSelectedUser(null);
    } catch (error) {
      logger.error('Password reset failed:', error);
    } finally {
      setIsResetting(false);
    }
  };

  const handleResetCancel = () => {
    setSelectedUser(null);
  };

  const handleRowClickInternal = (user: OrganizationUser) => {
    if (onRowClick) {
      onRowClick(user);
    }
  };

  return (
    <>
      <div className="rounded-lg border shadow-soft overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 text-left" />
              <TableHead
                className="cursor-pointer hover:!bg-accent/30 select-none text-left transition-colors duration-150"
                onClick={() => handleSort('username')}
              >
                Username{getSortIndicator('username')}
              </TableHead>
              <TableHead
                className="cursor-pointer hover:!bg-accent/30 select-none text-left transition-colors duration-150"
                onClick={() => handleSort('display_name')}
              >
                Display Name{getSortIndicator('display_name')}
              </TableHead>
              <TableHead
                className="cursor-pointer hover:!bg-accent/30 select-none text-left transition-colors duration-150"
                onClick={() => handleSort('email')}
              >
                Email{getSortIndicator('email')}
              </TableHead>
              <TableHead
                className="cursor-pointer hover:!bg-accent/30 select-none text-left transition-colors duration-150"
                onClick={() => handleSort('role')}
              >
                Role{getSortIndicator('role')}
              </TableHead>
              <TableHead className="text-left">Actions</TableHead>
              {onDelete && <TableHead className="w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeleton />
            ) : sortedUsers.length === 0 ? (
              <EmptyState />
            ) : (
              sortedUsers.map(user => (
                <TableRow
                  key={user.id}
                  className="cursor-pointer hover:!bg-accent/50 transition-colors duration-150"
                  onClick={() => handleRowClickInternal(user)}
                >
                  <TableCell>
                    <Avatar
                      src={user.profile_image}
                      displayName={user.display_name}
                      userId={user.id}
                      size={32}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{user.username}</TableCell>
                  <TableCell>{user.display_name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {user.email || (
                      <span className="text-muted-foreground/50 italic">
                        No email
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getRoleBadgeVariant(user.role as UserRole)}>
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={e =>
                          handleResetClick(e, user.id, user.email ?? null)
                        }
                      >
                        Reset Password
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Open profile in new tab"
                        onClick={e => {
                          e.stopPropagation();
                          window.open(
                            generateDeepLink(DeepLinkEntity.USER, user.id),
                            '_blank'
                          );
                        }}
                      >
                        <Icon name={IconName.LINK} size="sm" />
                      </Button>
                    </div>
                  </TableCell>
                  {onDelete && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-destructive/10"
                        onClick={e => {
                          e.stopPropagation();
                          onDelete(user);
                        }}
                        title={`Delete ${user.display_name}`}
                      >
                        <Icon
                          name={IconName.TRASH}
                          size="sm"
                          className="text-destructive/70"
                        />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Password Reset Popup */}
      {selectedUser && (
        <PasswordResetPopup
          isOpen={true}
          userEmail={selectedUser.email || ''}
          isLoading={isResetting}
          onSubmit={handleResetConfirm}
          onCancel={handleResetCancel}
        />
      )}
    </>
  );
}
